/**
 * CaptureSnapshot — fields LOCKED at capture time that don't exist at intent time.
 *
 * Used by `CheckoutPayload` as a structural extension of `CheckoutIntent`:
 *
 *   CheckoutPayload = CheckoutIntent & CaptureSnapshot   (per-branch, post-move-4)
 *
 * The composition makes Payload a structural superset of Intent — drift on
 * shared (Intent) axes is compile-impossible because they're literally one
 * declaration. The CaptureSnapshot side carries everything Intent does NOT
 * (computed totals from cart-math, server-only transport fields like
 * `paypalOrderId` / `captureIds` / `payeeMerchantIds`, the dispute-defence
 * `shippingAddressRaw`).
 *
 * Wire vs server-internal:
 *  - The buyer-emitted CAPTURE-channel payload (what the client POSTs to
 *    `/api/paypal-orders/capture` as `checkoutData`) carries the
 *    cart-math fields (`subtotal`/`shippingTotal`/`serviceFee`/`total`/
 *    `context`/`userId`/`currency`) — Zod parses these at the boundary
 *    via `CheckoutPayloadSchema`.
 *  - The server-only transport fields (`paypalOrderId`, `captureIds`,
 *    `actualPlatformFees`, `payeeMerchantIds`, `paymentProvider`,
 *    `providerPaymentId`, `isPartialOrder`, `shippingAddressRaw`) are
 *    added INSIDE the route handler by `buildOrderPayloadFromCapture()`
 *    (`lib/payments/paypal-capture-to-order.ts`) — they never appear on
 *    the wire. They're declared as optional here so a fresh client-emitted
 *    payload (no server fields yet) still parses; the route handler always
 *    fills them before calling `createOrder()`.
 *
 * History — Sibling-F · F2 closure (Option E2, 2026-06-27). Pre-E2,
 * `CheckoutPayload` was a parallel declaration to `CheckoutIntent` that
 * DRIFTED twice in 36h (HANDOFF_EXTERNAL_SHIPPING_CAPTURE_GAP_2026_06_26
 * + HANDOFF_PAYLOAD_UNION_DOWNSTREAM_2026_06_27). E2 eliminates the
 * parallel channel by making Payload structurally extend Intent through
 * intersection with this snapshot type.
 *
 * @see ./checkout-payload.ts — consumer (the intersection happens there)
 * @see ./checkout-intent.ts — the "Intent side" of the extension
 * @see .tondocs/RELIABILITY_ROADMAP.md §3a Sibling-F · F2
 * @see .tondocs/shippo/HANDOFF_RELIABILITY_GAPS_2026_06_27.md PR-E
 * @see .tondocs/skills/type-driven-design.md §6 (SSOT)
 */

import { z } from "zod";
import { CheckoutItemContextSchema, type CheckoutItemContext } from "./checkout-intent";
import { pinned } from "./pin";

/**
 * Server-only transport fields that are added by the route handler
 * (`buildOrderPayloadFromCapture` for PayPal, the Stripe webhook handler for
 * the Stripe rail) after the wire payload has been boundary-parsed. They are
 * NEVER part of the buyer-emitted payload — declaring them on `CaptureSnapshot`
 * is purely so the union of "wire shape ∪ server-rewritten shape" stays one
 * declaration, and so `createOrder` (which sees the server-rewritten form)
 * can take a typed `CheckoutPayload` argument.
 */
export interface CaptureSnapshotServerFields {
  /** PayPal Order ID. Set by capture route after PayPal `orders.capture`. */
  paypalOrderId?: string;
  /** sellerId → provider_capture_id (Stripe reuses session id). */
  captureIds?: Record<string, string>;
  /** sellerId → actual platform_fee from PayPal capture (may be < requested). */
  actualPlatformFees?: Record<string, number>;
  /** sellerId → payee.merchant_id snapshot from PayPal capture. */
  payeeMerchantIds?: Record<string, string>;
  paymentProvider?: "paypal" | "stripe";
  providerPaymentId?: string | null;
  isPartialOrder?: boolean;
  /**
   * Buyer shipping address SNAPSHOT for dispute / chargeback defence —
   * extracted verbatim from the payment-provider capture response. NOT
   * the buyer's `address_book` pick. Gap D1, 2026-06-25.
   *
   * @see lib/payments/paypal-capture-summary.ts::extractShippingAddressRaw
   * @see .tondocs/shippo/PLAN.md §A "Buyer shipping address snapshot"
   */
  shippingAddressRaw?: Record<string, unknown> | null;
}

/**
 * Capture-time snapshot fields the buyer sends on the wire (`context` /
 * `currency`) plus the `userId` (which the client supplies pre-auth-buffer,
 * may be null) and the server-only transport fields.
 *
 * `userId` carries `null` on the embed-page WebView pre-auth path; the route
 * handler ALWAYS overrides it with `auth.user.id` before `createOrder` runs.
 * The schema accepts `null` so a fresh wire payload parses; downstream the
 * server-rewritten value is always a string.
 *
 * CAPTURED_FACTS Phase E (2026-07-11): the cart-math totals
 * (`subtotal` / `shippingTotal` / `serviceFee` / `total`) were REMOVED from
 * this wire contract. `orders.{subtotal,shipping,service_fee,total}` are
 * derivation columns — the server recomputes them from validated items +
 * the CREATE-time wire snapshot at capture, and no longer trusts the client
 * CaptureSnapshot. In-flight drafts that still carry the old keys are
 * Zod-stripped on parse; the derivation takes over.
 * @see .tondocs/payments/CAPTURED_FACTS_PHASE_E_MINISPEC.md §1 (E6), §3
 */
export interface CaptureSnapshot extends CaptureSnapshotServerFields {
  userId: string | null;
  /** ISO-4217. Single currency per order. */
  currency: string;
  /**
   * Price-source discriminator at root. ORTHOGONAL to the `kind` axis
   * (shipping mode). Under the single-seller invariant every item in one
   * checkout shares the same context kind, so lifting it to root keeps the
   * type DRY without losing information. Intent items also carry `context`
   * (per-item) for resolver-side branching — Payload inherits that
   * structurally + persists the root copy for back-compat consumers
   * (`createOrder` destructures `body.context`).
   */
  context: CheckoutItemContext;
}

/**
 * Shape map for the server-only transport fields. Exported as a plain
 * `Record<keyof..., ZodType>` so the `CheckoutPayloadFlatRateSchema` and
 * `CheckoutPayloadLiveRateSchema` per-branch builders can spread it.
 */
export const captureSnapshotServerShape = {
  paypalOrderId: z.string().optional(),
  captureIds: z.record(z.string(), z.string()).optional(),
  actualPlatformFees: z.record(z.string(), z.number()).optional(),
  payeeMerchantIds: z.record(z.string(), z.string()).optional(),
  paymentProvider: z.enum(["paypal", "stripe"]).optional(),
  providerPaymentId: z.string().nullable().optional(),
  isPartialOrder: z.boolean().optional(),
  shippingAddressRaw: z.record(z.string(), z.unknown()).nullable().optional(),
} as const;

/**
 * Full snapshot shape map — both wire-side and server-side fields. Spread
 * into each `CheckoutPayload*RateSchema` branch (the discriminated union
 * cannot be assembled from `.merge()` of two `z.object` because the result
 * is no longer a `ZodObject` Zod's discriminator requires).
 */
export const captureSnapshotShape = {
  userId: z.string().nullable(),
  currency: z.string().min(1),
  // CAPTURED_FACTS Phase E: cart-math totals (subtotal/shippingTotal/
  // serviceFee/total) removed from the wire — server derives them at capture.
  // A fresh Zod object strips unknown keys, so in-flight drafts carrying the
  // old totals parse cleanly (values ignored). See MINISPEC §1 (E6).
  context: CheckoutItemContextSchema,
  ...captureSnapshotServerShape,
} as const;

export const CaptureSnapshotSchema = pinned<CaptureSnapshot>()(
  z.object(captureSnapshotShape),
);
